import { Formik, FormikHelpers } from "formik";
import React, { useCallback, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { ErrorsListCard } from "../../components/forms/errors-list";
import { TextFieldFormGroup } from "../../components/forms/groups/text-field-group";
import { SaveData } from "../../components/forms/save-data";
import { logError } from "../../errors";
import { SubmitState } from "../../util/loading";
import { getObject } from "../../util/local-storage";
import { FormState, StorageModel } from "../../components/forms/form-state";
import { apiConfiguration, withCsrf } from "../../api-configuration";
import styled from "styled-components";
import {
  AddressOption,
  AddressPickerFormGroup,
} from "../../components/forms/groups/address-picker-field-group";
import { LeadApi, CreateLeadForm } from "../../lib-api";
import { object, Schema, string } from "yup";
import { trackGaEvent } from "../../util/gtm";
import clientConfiguration from "../../client-configuration";

const storageKey = "user-lead-draft";

type Form = Omit<CreateLeadForm, "dawaAddressId"> & {
  dawaAddress?: AddressOption;
};

const leadFormValidationSchema: Schema<Form> = object().shape({
  email: string().label("Email").email().required(),
  phone: string().label("Telefon").required(),
  dawaAddress: object()
    .label("Addresse")
    .shape({
      value: string().label("Addresse").required(),
      label: string(),
    })
    .required(),
});

function getInitialValues(savedValues?: Form): Form {
  return {
    email: savedValues?.email ?? "",
    phone: savedValues?.phone ?? "",
    dawaAddress: savedValues?.dawaAddress ?? undefined,
  };
}

function getInitialState(savedState?: FormState): FormState {
  return {
    errors: savedState?.errors ?? undefined,
    state: SubmitState.None,
  };
}

const api = new LeadApi(apiConfiguration);

const Container = styled("div")<{ maxWidth?: string }>(({ maxWidth }) => ({
  backgroundColor: "#2b2d4c",
  maxWidth: maxWidth,
  margin: maxWidth ? "0 auto" : undefined,
  color: "#ffffff",
}));

export const LeadForm: React.FC<{
  csrf: string;
  forceMobile?: boolean;
  maxWidth?: string;
  redirectToBoligContent?: boolean;
  eventSection: string;
}> = ({
  csrf,
  forceMobile = false,
  maxWidth,
  redirectToBoligContent = false,
  eventSection,
}) => {
  const [savedData] = useState(getObject<StorageModel<Form>>(storageKey));
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formState, setFormState] = useState<FormState>(
    getInitialState(savedData?.state)
  );

  const handleSubmit = useCallback(
    async (values: Form, helpers: FormikHelpers<Form>) => {
      try {
        setFormState({
          state: SubmitState.None,
        });

        const result = await withCsrf(api, csrf).createLead({
          createLeadForm: {
            email: values.email,
            phone: values.phone,
            dawaAddressId: values.dawaAddress!.value,
          },
        });

        trackGaEvent(
          "lead_generated",
          {
            lead_type: "realtor_sales_assessment",
            in_section: eventSection,
          },
          {
            email: values.email,
            phone: values.phone,
            zip: values.dawaAddress?.data.postalCode,
            city: values.dawaAddress?.data.city,
            country: values.dawaAddress?.data.country,
          },
          (id) => {
            if (id == clientConfiguration.gtmId && redirectToBoligContent) {
              window.location.href = result.continueUrl;
            }
          }
        );

        helpers.resetForm();
        setFormState(getInitialState());
        setFormState({
          state: SubmitState.Submitted,
        });

        // this is in case gtm is not loaded
        if (redirectToBoligContent) {
          setTimeout(() => {
            window.location.href = result.continueUrl;
          }, 500);
        }
      } catch (e) {
        if (e instanceof Response && e.status == 400) {
          const json = await e.json();

          e = new Error(JSON.stringify(json));

          setFormState({
            state: SubmitState.Error,
            errors: json.errors,
          });
        } else {
          setFormState({
            state: SubmitState.Error,
          });
        }

        if (!(e instanceof Error)) {
          e = new Error(JSON.stringify(e));
        }
        logError(e);
      }
    },
    [setFormState]
  );

  return (
    <Formik<Form>
      initialValues={getInitialValues(savedData?.values)}
      onSubmit={handleSubmit}
      validationSchema={leadFormValidationSchema}
    >
      {({
        handleChange,
        values,
        touched,
        errors,
        isSubmitting,
        handleSubmit,
      }) => (
        <Container className="p-4 rounded-corners" maxWidth={maxWidth}>
          <SaveData<StorageModel<Form>>
            data={{ values, state: formState }}
            storageKey={storageKey}
          />
          {formState.state === SubmitState.Submitted ? (
            <>
              <h2>Tak for din henvendelse, du vil snart blive kontaktet...</h2>
            </>
          ) : (
            <>
              <Form onSubmit={handleSubmit}>
                <Row className="mb-2">
                  <Col>
                    <h2 className="d-none d-md-block">
                      FÅ TRE GRATIS SALGSVURDERINGER AF DIN BOLIG
                    </h2>
                    <h2 className="d-md-none">
                      FÅ TRE GRATIS SALGSVURDERINGER
                    </h2>
                  </Col>
                </Row>
                <Row>
                  <Col
                    className={`col-12 ${forceMobile ? "col-12" : "col-md-6"}`}
                  >
                    <TextFieldFormGroup
                      fieldName="phone"
                      fieldDisplayName="Telefonnummer *"
                      placeholder="Telefonnummer"
                      error={touched.phone ? errors.phone : undefined}
                      value={values.phone}
                      disabled={isSubmitting}
                      handleChange={handleChange}
                    />
                  </Col>
                  <Col
                    className={`col-sm-12 ${
                      forceMobile ? "col-12" : "col-md-6"
                    }`}
                  >
                    <TextFieldFormGroup
                      fieldName="email"
                      fieldDisplayName="Email *"
                      placeholder="Email"
                      error={touched.email ? errors.email : undefined}
                      value={values.email}
                      disabled={isSubmitting}
                      handleChange={handleChange}
                    />
                  </Col>
                </Row>
                <AddressPickerFormGroup
                  fieldName="dawaAddress"
                  fieldDisplayName="Adresse *"
                  placeholder="Adresse"
                  error={touched.dawaAddress ? errors.dawaAddress : undefined}
                  value={values.dawaAddress}
                  disabled={isSubmitting}
                  handleChange={handleChange}
                />
                <Row className="align-items-center">
                  <Col>
                    <small>
                      Dine oplysninger deles med op til tre lokale
                      ejendomsmæglere, som efterfølgende vil kontakte dig vedr.
                      salgsvurdering.
                    </small>
                  </Col>
                  <Col
                    className={`col-12 ${
                      forceMobile ? "col-12" : "col-md-auto"
                    }`}
                  >
                    <Form.Group>
                      <Button
                        className="align-self-end mt-3"
                        disabled={isSubmitting}
                        variant="primary"
                        block={false}
                        type="submit"
                        size="lg"
                        onClick={() => setSubmitAttempted(true)}
                      >
                        Bliv kontaktet
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
              {submitAttempted && <ErrorsListCard errors={errors} />}
            </>
          )}
          {formState.state == SubmitState.Error && (
            <div>
              {formState.errors ? (
                <ErrorsListCard
                  title="Vores server meldte følgende fejl"
                  errors={formState.errors}
                />
              ) : (
                <>Ups, der opstod en fejl. Prøv evt. at genindlæse siden.</>
              )}
            </div>
          )}
        </Container>
      )}
    </Formik>
  );
};
